import React from 'react';
import { useTheme } from '../context/ThemeContext';
import { fs } from '../utils/typography';
import { dismissInstallHint, isIosSafariInBrowser, useInstallPrompt } from '../utils/installPrompt';

// Tip for iPhone Safari: installed to the Home Screen, the site runs full
// screen with no browser bars (Safari's address + tab bars take roughly a
// quarter of a sideways phone, and the page can't hide them — Safari only
// collapses them when the page itself scrolls, and Home's page scroll is
// locked; see .no-page-scroll in index.css).
//
// Shown automatically on the home page, once (until "Got it"): right away on
// a sideways phone, where the space problem is worst, otherwise from the
// second visit on — not on a first visit, which already has the disclaimer
// banner and is often a one-off from a shared event link. Also opened on
// request from the menus' "Add to Home Screen" item (see requestInstall).
// Android isn't shown this: Chrome offers its own install prompt.
const InstallHint = ({ isHomePage, isCompactLandscape }) => {
    const { theme } = useTheme();
    const tokens = theme.tokens;
    const c = tokens.colors;
    const { hintOpenedFromMenu, hintDismissed, visitCount } = useInstallPrompt();

    const autoShow = isHomePage && isIosSafariInBrowser() && !hintDismissed && (isCompactLandscape || visitCount >= 2);
    if (!hintOpenedFromMenu && !autoShow) return null;

    return (
        <div
            role="status"
            style={{
                position: 'fixed', zIndex: 60, right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
                bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
                ...(isCompactLandscape ? { maxWidth: 330 } : { left: 'calc(env(safe-area-inset-left, 0px) + 12px)', maxWidth: 420, marginLeft: 'auto' }),
                background: c.surface, color: c.ink, border: `1px solid ${c.gridLine}`, borderLeft: `4px solid ${c.accent}`,
                borderRadius: 12, padding: '10px 12px', textAlign: 'left', fontFamily: tokens.fonts.ui,
                boxShadow: `0 8px 28px ${tokens.mode === 'dark' ? 'rgba(0,0,0,0.55)' : 'rgba(0,0,0,0.18)'}`,
            }}
        >
            <div style={{ fontSize: fs(13), fontWeight: 700, lineHeight: 1.3 }}>
                <i className="fas fa-expand mr-1.5" style={{ color: c.accent }} aria-hidden="true" />
                More room for the calendar
            </div>
            <p style={{ fontSize: fs(12), color: c.muted, lineHeight: 1.45, margin: '4px 0 0' }}>
                Tap <i className="fas fa-arrow-up-from-bracket" aria-label="Share" /> Share, then <strong style={{ color: c.ink }}>Add to Home Screen</strong> to
                open ATLTech.events full screen, without Safari&rsquo;s bars.
            </p>
            <p style={{ fontSize: fs(11), color: c.faint, lineHeight: 1.4, margin: '4px 0 0' }}>
                Or choose the Compact tab layout in Settings &rsaquo; Apps &rsaquo; Safari.
            </p>
            <div className="flex justify-end" style={{ marginTop: 8 }}>
                <button
                    type="button"
                    onClick={dismissInstallHint}
                    style={{
                        fontFamily: tokens.fonts.ui, fontSize: fs(12.5), fontWeight: 700, color: c.todayInk, background: c.accent,
                        border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer',
                    }}
                >
                    Got it
                </button>
            </div>
        </div>
    );
};

export default InstallHint;
